/**
 * VoiceSessionManager
 *
 * Chunks intermédiaires via {"action": "transcribe_only"} — ASR seul, ~1-2s.
 * Chunk final via {"action": "stop"} — pipeline complet (classification etc.).
 *
 * Audio streamé en continu sans buffering local :
 *   - Le micro ne s'interrompt jamais pendant l'ASR intermédiaire
 *   - Le backend accumule l'audio dans son propre buffer (is_recording=true)
 *   - "transcribe_only" vide le buffer backend et retourne le texte immédiatement
 *   - Le chunk suivant est schedulé après réception du résultat (pas de chevauchement)
 *
 * Cas stop() :
 *   - "stop" est envoyé → backend traite l'audio restant (après dernier transcribe_only)
 *   - Si buffer vide → backend répond "no_audio" → traité comme fin de session normale
 *
 * Contrat :
 *   manager.start()   → ouvre WS + micro, commence à streamer
 *   manager.stop()    → coupe le micro immédiatement, finalise en BG
 *   manager.destroy() → nettoyage forcé (unmount, nouvelle session)
 */

import { authService } from '@/features/auth/services/authService';
import { API_ENDPOINTS } from '@/config/api';

const CHUNK_DURATION_MS = 3_000;

// VAD — silence detection
// 4096 samples @ 16kHz = 256ms par frame
// ~0.77s / 0.256s ≈ 3 frames de silence consécutifs → auto-stop
const VAD_RMS_THRESHOLD = 0.008;       // niveau minimal pour détecter la parole
const VAD_MIN_SPEECH_FRAMES = 3;       // frames de parole min avant d'activer le stop (~0.75s)
const VAD_SILENCE_FRAMES_FOR_STOP = 3; // frames de silence → stop (~0.77s ; réduit de 4 → commandes plus rapides, Option 1)

// Commande vocale d'arrêt — détectée dans les transcriptions intermédiaires
const STOP_WORDS = ['stop'];

export interface SessionResult {
  fullText: string;
  classification: { type: string; confidence: number };
  structuredData: Record<string, unknown> | null;
  /** Durée totale de la session (capture → résultat final), en secondes */
  processingTime: number;
  /** Nombre de chunks constituant le résultat */
  chunkCount: number;
}

export interface SessionCallbacks {
  /** Appelé après chaque chunk intermédiaire — texte partiel cumulé */
  onChunkComplete: (partialText: string, chunkIndex: number) => void;
  /** Appelé une fois, quand toute la session est traitée */
  onSessionComplete: (result: SessionResult) => void;
  /** Appelé en cas d'erreur (WS, ASR, pipeline) */
  onError: (message: string) => void;
  /** « stop » = turn OFF (Option 1) : mot « stop » détecté en capture → ferme la
   *  session SANS la soumettre (≠ silence VAD qui, lui, soumet normalement). */
  onStopWord?: () => void;
}

export class VoiceSessionManager {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private stream: MediaStream | null = null;
  private chunkTimer: ReturnType<typeof setTimeout> | null = null;

  private stopped = false;
  private completed = false;

  /** Accumulation des textes de chaque chunk (intermédiaires + final) */
  private accumulatedTexts: string[] = [];
  /** Dernière classification reçue (chunk final) */
  private lastClassification: { type: string; confidence: number } | null = null;
  /** Dernières données structurées reçues (chunk final) */
  private lastStructuredData: Record<string, unknown> | null = null;

  private sessionStartTime = 0;

  /** VAD — compteurs pour la détection silence/parole */
  private vadSpeechFrames = 0;
  private vadSilenceFrames = 0;

  /** US-108 : mode dictée libre sans template prédéfini */
  private freeTemplate = false;

  /** US-FHIRW-002 : patient sélectionné, propagé au backend (UUID opaque). */
  private patientId: string | null = null;

  constructor(
    private readonly callbacks: SessionCallbacks,
    freeTemplate = false,
    patientId: string | null = null,
  ) {
    this.freeTemplate = freeTemplate;
    this.patientId = patientId;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // API publique
  // ─────────────────────────────────────────────────────────────────────────

  async start(): Promise<void> {
    const token = authService.getToken();
    if (!token) throw new Error('Non authentifié');

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: 16000,
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    this.ws = new WebSocket(API_ENDPOINTS.voiceWs(token));

    await new Promise<void>((resolve, reject) => {
      this.ws!.onopen = () => resolve();
      this.ws!.onerror = () => reject(new Error('Connexion WebSocket échouée'));
    });

    this.ws.onmessage = (e) => this.handleMessage(e);
    this.ws.onerror = () => {
      if (!this.completed) this.emitError('Connexion WebSocket perdue');
    };
    this.ws.onclose = (e) => {
      if (!this.completed && e.code !== 1000) {
        this.emitError('Connexion fermée de façon inattendue');
      }
    };

    this.audioContext = new AudioContext({ sampleRate: 16000 });
    const source = this.audioContext.createMediaStreamSource(this.stream);
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
    this.processor.onaudioprocess = (e) => this.handleAudio(e);
    source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);

    this.sessionStartTime = Date.now();
    this.send({ action: 'start', patient_id: this.patientId });
    this.scheduleChunkTimer();
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.stopAudio();
    // Finalisation ASR-seule : le dialogue guidé n'utilise pas le pipeline LLM du stop (pilier économie de tokens)
    this.send({ action: 'stop', free_template: this.freeTemplate, lightweight: true });
  }

  destroy(): void {
    if (this.completed) return;
    this.completed = true;
    this.stopped = true;
    this.stopAudio();
    this.ws?.close(1000, 'destroyed');
    this.ws = null;
  }

  /** « stop » = turn OFF (Option 1) : ferme la session SANS émettre de résultat
   *  (ne soumet pas). Tear-down complet + signale `onStopWord` à l'orchestrateur. */
  private abortByStopWord(): void {
    if (this.completed) return;
    this.completed = true;
    this.stopped = true;
    this.stopAudio();
    this.ws?.close(1000, 'stopword');
    this.ws = null;
    this.callbacks.onStopWord?.();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Capture audio — streaming continu + VAD auto-stop
  // ─────────────────────────────────────────────────────────────────────────

  private handleAudio(e: AudioProcessingEvent): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.stopped) return;

    const float32 = e.inputBuffer.getChannelData(0);

    // VAD : calcul RMS du frame courant
    let sumSquares = 0;
    for (let i = 0; i < float32.length; i++) sumSquares += float32[i] * float32[i];
    const rms = Math.sqrt(sumSquares / float32.length);

    if (rms >= VAD_RMS_THRESHOLD) {
      // Parole détectée
      this.vadSpeechFrames++;
      this.vadSilenceFrames = 0;
    } else if (this.vadSpeechFrames >= VAD_MIN_SPEECH_FRAMES) {
      // Silence après parole suffisante
      this.vadSilenceFrames++;
      if (this.vadSilenceFrames >= VAD_SILENCE_FRAMES_FOR_STOP) {
        // Auto-stop VAD : ~0.77s de silence après parole (fin d'énoncé)
        this.stop();
        return;
      }
    }

    const pcm = this.float32ToPCM16(float32);
    this.ws.send(pcm);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Messages WebSocket backend + commande vocale "stop"
  // ─────────────────────────────────────────────────────────────────────────

  private containsStopWord(text: string): boolean {
    const words = text.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/);
    return STOP_WORDS.some((sw) => words.includes(sw));
  }

  private cleanStopWords(text: string): string {
    return STOP_WORDS
      .reduce((t, sw) => t.replace(new RegExp(`\\b${sw}\\b`, 'gi'), ''), text)
      .replace(/\s+/g, ' ')
      .trim();
  }

  private handleMessage(event: MessageEvent): void {
    let message: Record<string, unknown>;
    try {
      message = JSON.parse(event.data as string);
    } catch {
      return;
    }

    if (message['step'] === 'transcription_partial') {
      // ── Segment intermédiaire (dans le chunk courant) ──────────────────────
      const text = ((message['text'] as string) || '').trim();

      // Commande vocale "stop" = turn OFF → ferme la session sans la soumettre
      if (!this.stopped && this.containsStopWord(text)) {
        this.abortByStopWord();
        return;
      }

      const displayText = this.accumulatedTexts.length > 0
        ? this.accumulatedTexts.join(' ') + ' ' + text
        : text;
      this.callbacks.onChunkComplete(displayText.trim(), this.accumulatedTexts.length);

    } else if (message['step'] === 'transcribe_only') {
      // ── Fin de chunk intermédiaire : ASR seul, pas de LLM ─────────────────
      const raw = ((message['text'] as string) || '').trim();
      const hasStop = !this.stopped && this.containsStopWord(raw);
      const text = this.cleanStopWords(raw);

      if (text) this.accumulatedTexts.push(text);
      this.callbacks.onChunkComplete(
        this.accumulatedTexts.join(' '),
        this.accumulatedTexts.length,
      );

      // Commande "stop" = turn OFF → ferme la session sans la soumettre
      if (hasStop) {
        this.abortByStopWord();
        return;
      }

      // Prochain chunk schedulé ici (après résultat → pas de chevauchement)
      if (!this.stopped) {
        this.scheduleChunkTimer();
      }

    } else if (message['step'] === 'complete') {
      // ── Chunk final (après stop()) ────────────────────────────────────────
      const raw = ((message['text'] as string) || '').trim();
      // « stop » dans le chunk final = turn OFF → fermeture, pas de soumission.
      if (this.containsStopWord(raw)) {
        this.abortByStopWord();
        return;
      }
      const text = this.cleanStopWords(raw);
      if (text) this.accumulatedTexts.push(text);
      if (message['classification']) {
        this.lastClassification = message['classification'] as { type: string; confidence: number };
      }
      if ('structured_data' in message) {
        this.lastStructuredData = (message['structured_data'] as Record<string, unknown>) ?? null;
      }
      this.emitFinalResult();

    } else if (message['step'] === 'error') {
      const errorType = message['error_type'] as string | undefined;
      if (errorType === 'no_audio' && this.stopped) {
        // Buffer vide au moment du stop → émettre ce qu'on a accumulé
        this.emitFinalResult();
      } else {
        this.emitError((message['message'] as string) || 'Erreur pipeline vocale');
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Timer chunk
  // ─────────────────────────────────────────────────────────────────────────

  private scheduleChunkTimer(): void {
    if (this.chunkTimer) clearTimeout(this.chunkTimer);
    this.chunkTimer = setTimeout(() => {
      if (this.stopped) return;
      this.send({ action: 'transcribe_only' });
      // Le prochain chunk sera schedulé dans handleMessage après réception
    }, CHUNK_DURATION_MS);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Émission des résultats
  // ─────────────────────────────────────────────────────────────────────────

  private emitFinalResult(): void {
    if (this.completed) return;
    this.completed = true;

    const fullText = this.accumulatedTexts.join(' ').trim();
    const processingTime = (Date.now() - this.sessionStartTime) / 1000;

    this.ws?.close(1000, 'session complete');

    this.callbacks.onSessionComplete({
      fullText,
      classification: this.lastClassification ?? { type: 'observation_libre', confidence: 0 },
      structuredData: this.lastStructuredData,
      processingTime,
      chunkCount: this.accumulatedTexts.length,
    });
  }

  private emitError(message: string): void {
    if (this.completed) return;
    this.completed = true;
    this.callbacks.onError(message);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Utilitaires
  // ─────────────────────────────────────────────────────────────────────────

  private stopAudio(): void {
    if (this.chunkTimer) { clearTimeout(this.chunkTimer); this.chunkTimer = null; }
    if (this.processor) { this.processor.disconnect(); this.processor = null; }
    if (this.audioContext) { this.audioContext.close(); this.audioContext = null; }
    if (this.stream) { this.stream.getTracks().forEach((t) => t.stop()); this.stream = null; }
  }

  private send(data: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  private float32ToPCM16(input: Float32Array): ArrayBuffer {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return output.buffer;
  }
}
